import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  HardDrive,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  Activity,
  Zap,
  TrendingUp,
  FileText,
  Database,

} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { ItemsPerPageDropdown } from '../../../components/ItemsPerPageDropdown ';
import { CustomDropdown } from '../../../components/CustomDropdown ';
import { PostgresFileIOView } from './PostgresFileIOView';
import { MySQLFileIOView } from './MySQLFileIOView';

interface MSSQLFileIOViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

// const PALETTE = [
//   '#3b82f6', 
//   '#10b981', 
//   '#f59e0b', 
//   '#ef4444', 
//   '#8b5cf6', 
//   '#06b6d4',
//   '#ec4899', 
//   '#14b8a6', 
//   '#f97316', 
//   '#84cc16', 
//   '#6366f1', 
//   '#e11d48', 
//   '#a855f7', 
//   '#0ea5e9', 
//   '#d97706', 
//   '#059669', 
//   '#7c3aed', 
//   '#db2777', 
//   '#4f46e5', 
//   '#2563eb', 
//   '#64748b', 
// ];

// const ScrollableLegend = (props: any) => {
//   const { payload, hoveredSeries, onHoverSeries } = props;
//   if (!payload || payload.length === 0) return null;
//   return (
//     <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-16 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
//       {payload.map((entry: any, index: number) => {
//         const seriesKey = entry.dataKey || entry.value;
//         const isHovered = hoveredSeries === seriesKey;
//         const isDimmed = hoveredSeries !== null && !isHovered;
//         return (
//           <div
//             key={`legend-item-${index}`}
//             onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
//             onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
//             className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
//               }`}
//           >
//             <span
//               className="w-2 h-2 rounded-full inline-block shrink-0"
//               style={{ backgroundColor: entry.color }}
//             />
//             <span
//               className="text-gray-700 dark:text-gray-200 text-[10px] truncate max-w-[160px] font-mono"
//               title={entry.value}
//             >
//               {entry.value}
//             </span>
//           </div>
//         );
//       })}
//     </div>
//   );
// };

// const ChartTooltip = ({ active, payload, label }: any) => {
//   if (active && payload && payload.length) {
//     return (
//       <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/80 rounded-lg p-2.5 shadow-xl text-[11px] text-gray-900 dark:text-gray-100 flex flex-col gap-1.5 min-w-[130px] z-50">
//         {label !== undefined && label !== null && label !== "" && (
//           <div className="text-gray-700 dark:text-gray-300 font-bold border-b border-gray-100 dark:border-gray-800 pb-1 mb-0.5 text-[11px] truncate">
//             {label}
//           </div>
//         )}
//         {payload.map((entry: any, index: number) => (
//           <div key={index} className="flex justify-between items-center gap-4">
//             <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-medium">
//               <span
//                 className="inline-block w-2 h-2 rounded-full shrink-0"
//                 style={{ backgroundColor: entry.color || entry.stroke || entry.fill || "#f97316" }}
//               />
//               {entry.name || entry.dataKey}
//             </span>
//             <span className="font-bold text-gray-900 dark:text-white font-mono">
//               {typeof entry.value === 'number' ? entry.value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : (entry.value ?? '-')}
//             </span>
//           </div>
//         ))}
//       </div>
//     );
//   }
//   return null;
// };

const MSSQLFileIOViewInternal: React.FC<MSSQLFileIOViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
  dbType,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [fileIOData, setFileIOData] = useState<any>(null);
  const [selectedDbFilter, setSelectedDbFilter] = useState<string>('all');
  // const [selectedFileFilter, setSelectedFileFilter] = useState<string>('all');
  const [tableSearchQuery, setTableSearchQuery] = useState<string>('');
  // const [hoveredAll, setHoveredAll] = useState<string | null>(null);

  // Pagination for Database Files table
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60', 10);
    const params: any = {
      cluster_id: activeCluster,

      minutes,
    };
    if (clientId) {
      params.client_id = clientId;
    }

    try {
      const res = await baseapi.get('/db_monitor_dashboard/graph/mssql/file_io', { params });
      setFileIOData(res.data?.data || res.data);
    } catch (err) {
      console.warn('MSSQLFileIOView fetch error:', err);
      setFileIOData(null);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // useEffect(() => {
  //   setSelectedFileFilter('all');
  // }, [selectedDbFilter]);

  const labels: string[] = fileIOData?.labels || [];
  const series: Record<string, (number | null)[]> = fileIOData?.series || {};
  const groups: string[] = fileIOData?.groups || Object.keys(series);
  const summary = fileIOData?.summary;

  const effectiveDbType = (dbType || '').toLowerCase();
  const isMySQL = effectiveDbType.includes('mysql') || (activeCluster || '').toLowerCase().includes('mysql');


  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const minutes = parseInt(timeRange || '60', 10);
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  const timeLabels = formatTimeLabels(labels);

  // Formatted data points for recharts
  // const formattedData = useMemo(() => {
  //   if (!labels || labels.length === 0) return [];
  //   return labels.map((_, idx) => {
  //     const point: Record<string, any> = { time: timeLabels[idx] };
  //     groups.forEach((g) => {
  //       const val = series[g]?.[idx];
  //       point[g] = val !== undefined && val !== null ? Number(Number(val).toFixed(2)) : 0;
  //     });
  //     return point;
  //   });
  // }, [labels, series, groups, timeLabels]);

  // const hasData = (arr: any[]) => {
  //   if (!arr || arr.length === 0) return false;
  //   return groups.length > 0 && arr.length > 0;
  // };

  // const getOpacity = (hovered: string | null, key: string) => {
  //   if (hovered === null) return 1;
  //   return hovered === key ? 1 : 0.15;
  // };

  // Available database options from summary or files
  const availableDatabases = useMemo(() => {
    const dbs = new Set<string>();
    if (summary?.database_distribution) {
      summary.database_distribution.forEach((d: any) => {
        if (d.database && d.database.trim()) dbs.add(d.database);
      });
    }
    if (summary?.files) {
      summary.files.forEach((f: any) => {
        if (f.database && f.database.trim()) dbs.add(f.database);
      });
    }
    return Array.from(dbs).sort();
  }, [summary]);

  const dbDropdownOptions = useMemo(() => {
    return [
      { label: 'All Databases', value: 'all' },
      ...availableDatabases.map((db) => ({ label: db, value: db })),
    ];
  }, [availableDatabases]);

  // Filter groups for chart based on selected DB
  // const filteredChartGroups = useMemo(() => {
  //   if (selectedDbFilter === 'all') return groups;
  //   if (!summary?.files) return groups;

  //   const fileNamesInDb = new Set(
  //     summary.files
  //       .filter((f: any) => f.database && f.database.toLowerCase() === selectedDbFilter.toLowerCase())
  //       .map((f: any) => f.file_name)
  //   );

  //   const filtered = groups.filter((g) => fileNamesInDb.has(g));
  //   return filtered.length > 0 ? filtered : groups;
  // }, [groups, selectedDbFilter, summary]);

  // const fileDropdownOptions = useMemo(() => {
  //   return [
  //     { label: `All Files (${filteredChartGroups.length})`, value: 'all' },
  //     ...filteredChartGroups.map((f) => ({ label: f, value: f })),
  //   ];
  // }, [filteredChartGroups]);

  // const chartVisibleGroups = useMemo(() => {
  //   if (selectedFileFilter === 'all') {
  //     return filteredChartGroups;
  //   }
  //   return [selectedFileFilter];
  // }, [selectedFileFilter, filteredChartGroups]);

  // Database files list
  const allFilesList: any[] = useMemo(() => {
    if (Array.isArray(summary?.files) && summary.files.length > 0) {
      return summary.files;
    }
    // Fallback if summary.files not provided
    return groups.map((g) => {
      const arr = series[g] || [];
      const nonNulls = arr.filter((v): v is number => v !== null && v !== undefined);
      const curr = nonNulls.length > 0 ? nonNulls[nonNulls.length - 1] : 0;
      const peak = nonNulls.length > 0 ? Math.max(...nonNulls) : 0;
      const sum = nonNulls.reduce((acc, v) => acc + v, 0);
      const avg = nonNulls.length > 0 ? sum / nonNulls.length : 0;
      const isLog = g.toLowerCase().includes('log') || g.toLowerCase().includes('redo');
      return {
        file_name: g,
        database: isMySQL ? 'mysql' : 'mssql',
        type: isLog ? 'LOG' : 'DATA',
        current_kbps: curr,
        average_kbps: avg,
        peak_kbps: peak,
        samples: nonNulls.length,
        total_samples: labels.length,
        status: peak > 1000 ? 'High' : peak > 100 ? 'Elevated' : 'Normal',
      };
    });
  }, [summary, groups, series, labels, isMySQL]);

  // Filtered files by database & table search query
  const filteredFiles = useMemo(() => {
    return allFilesList.filter((f) => {
      const matchesDb =
        selectedDbFilter === 'all' ||
        (f.database && f.database.toLowerCase() === selectedDbFilter.toLowerCase());
      const matchesSearch =
        !tableSearchQuery.trim() ||
        (f.file_name && f.file_name.toLowerCase().includes(tableSearchQuery.toLowerCase().trim())) ||
        (f.database && f.database.toLowerCase().includes(tableSearchQuery.toLowerCase().trim()));
      return matchesDb && matchesSearch;
    });
  }, [allFilesList, selectedDbFilter, tableSearchQuery]);

  // Paginated files
  const totalPages = Math.ceil(filteredFiles.length / itemsPerPage) || 1;
  const paginatedFiles = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredFiles.slice(start, start + itemsPerPage);
  }, [filteredFiles, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDbFilter, tableSearchQuery, itemsPerPage]);

  const formatKBps = (kbps: number | undefined | null) => {
    if (kbps === undefined || kbps === null || isNaN(kbps)) return '0.00 KB/s';
    if (kbps >= 1024 * 1024) {
      return `${(kbps / (1024 * 1024)).toFixed(2)} GB/s`;
    }
    if (kbps >= 1024) {
      return `${(kbps / 1024).toFixed(2)} MB/s`;
    }
    return `${Number(kbps).toFixed(2)} KB/s`;
  };

  const getStatusBadge = (status: string) => {
    const st = (status || 'Normal').toLowerCase();
    if (st === 'high') {
      return (
        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-red-50 dark:bg-red-500/20 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-500/30">
          High
        </span>
      );
    }
    if (st === 'elevated') {
      return (
        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-500/30">
          Elevated
        </span>
      );
    }
    return (
      <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-500/30">
        Normal
      </span>
    );
  };

  const getTypeBadge = (type: string) => {
    const t = (type || 'DATA').toUpperCase();
    if (t === 'LOG') {
      return (
        <span className="inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-purple-50 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30">
          LOG
        </span>
      );
    }
    return (
      <span className="inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-blue-50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30">
        DATA
      </span>
    );
  };

  // Max peak in database distribution for progress bars
  const maxDbPeak = useMemo(() => {
    if (!summary?.database_distribution || summary.database_distribution.length === 0) return 1;
    const max = Math.max(...summary.database_distribution.map((d: any) => Number(d.peak_kbps) || 0));
    return max > 0 ? max : 1;
  }, [summary]);

  if (loading) {
    return (
      <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[350px]">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Top Banner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-orange-500 text-white shadow-md">
            <HardDrive className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">File I/O</h2>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">Database file I/O throughput and activity monitoring</p>
          </div>
        </div>

        {/* Header Right: Status Badge & Sample Info */}
        <div className="flex items-center gap-3">
          <div className="text-[11px] text-gray-500 dark:text-gray-400 hidden sm:block">
            <span className="font-mono text-gray-700 dark:text-gray-200 font-semibold">{labels.length}</span> samples
            {labels.length > 0 && (
              <>
                {' '}• Last sample: <span className="font-mono text-gray-700 dark:text-gray-200">{timeLabels[timeLabels.length - 1]}</span>
              </>
            )}
          </div>
          {labels.length > 0 ?
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-500/30 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Monitoring Active
            </span> :
            <span className='text-gray-700 text-xs dark:text-gray-200 font-semibold'> No Samples </span>
          }
        </div>
      </div>

      {/* Top 4 KPI Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* CURRENT I/O */}
        <div className="bg-gray-50/70 dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-md flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                CURRENT I/O
              </span>
              <Activity className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
            </div>
            <div className="text-xl font-bold font-mono text-gray-900 dark:text-white">
              {formatKBps(summary?.current_io_kbps ?? 0)}
            </div>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">Latest recorded throughput</div>
        </div>

        {/* PEAK I/O */}
        <div className="bg-orange-50/70 dark:bg-gray-900 border border-orange-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-md flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-orange-700 dark:text-gray-400 uppercase tracking-wider">
                PEAK I/O
              </span>
              <Zap className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
            </div>
            <div className="text-xl font-bold font-mono text-orange-600 dark:text-orange-400">
              {formatKBps(summary?.peak_io_kbps ?? 0)}
            </div>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">Maximum observed throughput</div>
        </div>

        {/* AVERAGE I/O */}
        <div className="bg-blue-50/70 dark:bg-gray-900 border border-blue-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-md flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-blue-700 dark:text-gray-400 uppercase tracking-wider">
                AVERAGE I/O
              </span>
              <TrendingUp className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
            </div>
            <div className="text-xl font-bold font-mono text-blue-600 dark:text-blue-400">
              {formatKBps(summary?.average_io_kbps ?? 0)}
            </div>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">Average across samples</div>
        </div>

        {/* ACTIVE FILES */}
        <div className="bg-emerald-50/70 dark:bg-gray-900 border border-emerald-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-md flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-emerald-700 dark:text-gray-400 uppercase tracking-wider">
                ACTIVE FILES
              </span>
              <FileText className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
            </div>
            <div className="text-xl font-bold font-mono text-emerald-600 dark:text-emerald-400">
              {summary?.active_files ?? allFilesList.filter((f) => f.peak_kbps > 0).length}
            </div>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">Files with recorded I/O</div>
        </div>
      </div>

      {/* Main Full-Width Chart: File I/O Throughput */}
      {/* <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2.5">
          <div>
            <div className="flex items-center gap-1.5">
              <BarChart2 className="w-4 h-4 text-orange-500 dark:text-orange-400" />
              <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                File I/O Throughput
              </h4>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Read/write activity by database file</p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
           


       
            {filteredChartGroups.length > 0 && (
              <CustomDropdown
                data={fileDropdownOptions}
                selectedValue={selectedFileFilter}
                onSelect={(val) => setSelectedFileFilter(val)}
                width="w-52 sm:w-64"
                placeholder="Select File"
                buttonHeight={8}
              />
            )}
          </div>
        </div>

        <div className="h-72 w-full flex items-center justify-center">
          {hasData(formattedData) ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={formattedData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" opacity={0.2} />
                <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={5}>
                  <Label value="I/O Throughput (KB/s)" angle={-90} position="insideLeft" offset={10} fill="#9ca3af" fontSize={10} />
                </YAxis>
                <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                <Legend
                  content={
                    <ScrollableLegend
                      hoveredSeries={hoveredAll}
                      onHoverSeries={setHoveredAll}
                    />
                  }
                />
                {chartVisibleGroups.map((key) => {
                  const originalIdx = groups.indexOf(key);
                  const color = PALETTE[(originalIdx >= 0 ? originalIdx : 0) % PALETTE.length];
                  return (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={key}
                      stroke={color}
                      strokeOpacity={getOpacity(hoveredAll, key)}
                      strokeWidth={hoveredAll === key ? 2.5 : 1.5}
                      dot={false}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <span className="text-xs text-gray-400 dark:text-gray-500 italic">No file I/O telemetry available</span>
          )}
        </div>
      </div> */}

      <div className='flex justify-end'>
        {availableDatabases.length > 0 && (
          <CustomDropdown
            data={dbDropdownOptions}
            selectedValue={selectedDbFilter}
            onSelect={(val) => setSelectedDbFilter(val)}
            width="w-40 sm:w-44"
            placeholder="Database"
            buttonHeight={8}
          />
        )}
      </div>
      {/* Middle Row: Database I/O Distribution & Highest I/O Files */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Database I/O Distribution */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2 mb-3">
              <div className="flex items-center gap-1.5">
                <Database className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  Database I/O Distribution
                </h4>
              </div>
            </div>

            <div className="space-y-3.5 max-h-64 overflow-y-auto custom-scroll pr-1">
              {summary?.database_distribution && summary.database_distribution.length > 0 ? (
                summary.database_distribution.map((d: any, idx: number) => {
                  const pct = Math.min(100, Math.max(2, (Number(d.peak_kbps || 0) / maxDbPeak) * 100));
                  return (
                    <div key={idx} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-gray-800 dark:text-gray-200">{d.database}</span>
                        <span className="font-mono text-gray-500 dark:text-gray-400 text-[11px]">
                          {formatKBps(d.peak_kbps)} <span className="text-[10px] text-gray-400 dark:text-gray-500">peak</span>
                        </span>
                      </div>
                      <div className="h-2 w-full bg-gray-100 dark:bg-gray-900 rounded-full overflow-hidden border border-gray-200 dark:border-gray-800">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-300"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-xs text-gray-400 dark:text-gray-500 italic py-6 text-center">
                  No database distribution data available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Highest I/O Files */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2 mb-3">
              <div className="flex items-center gap-1.5">
                <Zap className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  Highest I/O Files
                </h4>
              </div>
            </div>

            <div className="space-y-2.5 max-h-64 overflow-y-auto custom-scroll pr-1">
              {summary?.highest_io_files && summary.highest_io_files.length > 0 ? (
                summary.highest_io_files.map((hf: any, idx: number) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800/60 pb-2 last:border-0 last:pb-0"
                  >
                    <div>
                      <div className="text-xs font-bold text-gray-900 dark:text-white font-mono">{hf.file_name}</div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">{hf.database || 'System / Engine'}</div>
                    </div>
                    <div className="text-xs font-bold font-mono text-orange-600 dark:text-orange-400">
                      {formatKBps(hf.peak_kbps)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-xs text-gray-400 dark:text-gray-500 italic py-6 text-center">
                  No high I/O files telemetry available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Table: Database Files with Local Pagination */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-700/50 pb-2.5">
          <div>
            <div className="flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
              <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                Database Files
              </h4>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">File-level throughput statistics</p>
          </div>

          {/* Search Input for Files */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search file or db..."
              value={tableSearchQuery}
              onChange={(e) => setTableSearchQuery(e.target.value)}
              className="bg-gray-50 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-7 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-orange-500/50 w-56 h-8"
            />
            {tableSearchQuery && (
              <button
                onClick={() => setTableSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto overflow-y-auto custom-scroll border border-gray-200 dark:border-gray-700/50 rounded-lg">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900 shadow-sm text-[11px] text-gray-600 dark:text-gray-400 uppercase tracking-wider select-none">
                <th className="py-2.5 px-3.5 bg-gray-50 dark:bg-gray-900">File</th>
                <th className="py-2.5 px-3.5 bg-gray-50 dark:bg-gray-900">Type</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Current</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Average</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Peak</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Samples</th>
                <th className="py-2.5 px-3.5 text-center bg-gray-50 dark:bg-gray-900">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30 text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900/40">
              {paginatedFiles && paginatedFiles.length > 0 ? (
                paginatedFiles.map((f, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                    <td className="py-2.5 px-3.5">
                      <div className="font-bold text-gray-900 dark:text-gray-200 font-mono">{f.file_name}</div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">{f.database || 'System / Engine'}</div>
                    </td>
                    <td className="py-2.5 px-3.5">{getTypeBadge(f.type)}</td>
                    <td className="py-2.5 px-3.5 text-right font-mono text-gray-900 dark:text-gray-200">
                      {formatKBps(f.current_kbps)}
                    </td>
                    <td className="py-2.5 px-3.5 text-right font-mono text-gray-700 dark:text-gray-200">
                      {formatKBps(f.average_kbps)}
                    </td>
                    <td className="py-2.5 px-3.5 text-right font-mono text-orange-600 dark:text-orange-400 font-bold">
                      {formatKBps(f.peak_kbps)}
                    </td>
                    <td className="py-2.5 px-3.5 text-right font-mono text-gray-700 dark:text-gray-300">
                      {f.samples} / {f.total_samples || labels.length}
                    </td>
                    <td className="py-2.5 px-3.5 text-center">{getStatusBadge(f.status)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-gray-500 dark:text-gray-400 italic">
                    {tableSearchQuery
                      ? `No files matching "${tableSearchQuery}".`
                      : 'No database files statistics available.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Local Pagination Controls */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-3">
            <span>
              Showing{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {filteredFiles.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}
              </span>{' '}
              to{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {Math.min(currentPage * itemsPerPage, filteredFiles.length)}
              </span>{' '}
              of <span className="font-semibold text-gray-900 dark:text-white">{filteredFiles.length}</span> files
            </span>

            <div className="flex items-center gap-2 border-l border-gray-200 dark:border-gray-800 pl-3">
              <span className="text-[11px] text-gray-500 dark:text-gray-400">Per page:</span>
              <ItemsPerPageDropdown
                value={itemsPerPage}
                onChange={(val: any) => {
                  setItemsPerPage(Number(val));
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
              title="First Page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
              title="Previous Page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <span className="px-2 py-0.5 text-xs text-gray-700 dark:text-gray-300 font-medium">
              Page <span className="text-gray-900 dark:text-white font-bold">{currentPage}</span> of{' '}
              <span className="text-gray-900 dark:text-white font-bold">{totalPages}</span>
            </span>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
              title="Next Page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
              title="Last Page"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const MSSQLFileIOView: React.FC<MSSQLFileIOViewProps> = (props) => {
  const effectiveDbType = (props.dbType || '').toLowerCase();
  const isPostgres =
    ['postgres', 'postgresql'].includes(effectiveDbType) ||
    effectiveDbType.includes('postgres') ||
    (props.activeCluster || '').toLowerCase().includes('postgres') ||
    (props.activeCluster || '').toLowerCase().startsWith('pg-');

  if (isPostgres) {
    return <PostgresFileIOView {...props} />;
  }

  const isMySQL =
    ['mysql', 'mariadb'].includes(effectiveDbType) ||
    effectiveDbType.includes('mysql') ||
    (props.activeCluster || '').toLowerCase().includes('mysql');

  if (isMySQL) {
    return <MySQLFileIOView {...props} />;
  }

  return <MSSQLFileIOViewInternal {...props} />;
};

export default MSSQLFileIOView;
